import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { filter, get, includes, kebabCase, omit } from 'lodash';

import facilitiesResource from '@lib/resources/facilitiesResource';
import { EntityID } from '@lib/resources/types';
import storageService from '@lib/services/storageService';

import { STORAGE_KEYS } from 'utils/constants';

import FilterList, { FilterListProps } from '../FilterList';

import { resultKey, selectFacilitiesFromPage } from './selectors';

interface Props extends FilterListProps {
  itemsIds?: EntityID[];
  respectSchedulerSetting?: boolean;
}

const FilterListFacilities: React.FC<Props> = ({
  itemsIds,
  idsExcluded,
  limit,
  respectSchedulerSetting = false,
  ...other
}) => {
  const PSFilter = storageService.get(STORAGE_KEYS.patientFormPSFilters);
  const facilitiesFromPSFilter = get(PSFilter, 'facility', []);
  const facilities = useSelector(facilitiesResource.selectEntitiesObj);
  const allFacilitiesList = Object.values(facilities);
  const dispatch = useDispatch();

  const shouldUseItemsFromProp = !!itemsIds;
  const itemsFromProp = filter(facilities, ({ id }) => includes(itemsIds, id));
  const [searchTerm, setSearchTerm] = useState<string | undefined>('');
  const [isSearching, setIsSearching] = useState(false);
  const filteredItemsFromProp = searchTerm
    ? filter(itemsFromProp, (item) => includes(kebabCase(item.name), kebabCase(searchTerm)))
    : itemsFromProp;

  const itemsFromPageRequest = useSelector(selectFacilitiesFromPage);

  const filteredFacilitiesFromPSFilter = useMemo(() => {
    const filteredFacilitiesRequestFromPSFilter =
      facilitiesFromPSFilter.length > 0
        ? itemsFromPageRequest.filter((facility) => facilitiesFromPSFilter.includes(facility.id))
        : itemsFromPageRequest;

    const filteredAllFacilitiesFromPSFilter =
      facilitiesFromPSFilter.length > 0
        ? allFacilitiesList.filter((facility) => facilitiesFromPSFilter.includes(facility.id))
        : allFacilitiesList;

    return isSearching ? filteredFacilitiesRequestFromPSFilter : filteredAllFacilitiesFromPSFilter;
  }, [isSearching, allFacilitiesList, facilitiesFromPSFilter, itemsFromPageRequest]);

  useEffect(() => {
    dispatch(
      facilitiesResource.fetchBulk({
        ids: itemsIds || idsExcluded,
      }),
    );
  }, [dispatch, itemsIds, idsExcluded]);

  return (
    <FilterList
      hideSearchField={get(other, 'hideSearchField', false)}
      itemsLabel="facilities"
      allItems={facilities}
      items={shouldUseItemsFromProp ? filteredItemsFromProp.slice(0, limit) : filteredFacilitiesFromPSFilter}
      idsExcluded={idsExcluded}
      // @ts-ignore
      onSearchChange={(page, callLimit, name) => {
        if (shouldUseItemsFromProp) {
          setSearchTerm(name);
        } else {
          const params: any = { page, limit: callLimit, name };

          if (respectSchedulerSetting) {
            params['settings.scheduler.enabled'] = true;
          }

          setIsSearching(!!name);
          dispatch(
            facilitiesResource.fetchPage({
              params,
              meta: {
                resultKey,
              },
            }),
          );
        }
      }}
      limit={limit}
      {...omit(other, 'allItems', 'items')}
    />
  );
};

export default FilterListFacilities;
